import { Space, Skeleton, message, Popover } from 'antd';
import styled from 'styled-components';
import type { ColumnsType } from 'antd/lib/table';
import { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PlusOutlined } from '@ant-design/icons';

import { getInvoiceNumbers } from 'services/account-settings';
import InvoicePrefixesForm from 'pages/Dialogs/InvoicePrefixesForm';
import { INVOICE_NUMBERS } from 'events/types';

import BasicTable from '../../components/table/basic';
import useTranslation from '../../customHooks/translate';

const IdentifierChars = () => {
  const translation = useTranslation();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [prefixToUpdate, setPrefixToUpdate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  const columns: ColumnsType<any> = [
    {
      title: <span>{translation.invoiceChars.InvoiceChar}</span>,
      dataIndex: 'partOfInvoiceName',
      key: 'name',
      width: 300,
      align: 'center',
    },
    {
      title: <span>{translation.invoiceChars.Action}</span>,
      key: 'action',
      width: 200,
      align: 'center',
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setPrefixToUpdate(record);
                    setAddModalOpen(true);
                  }}
                />
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle" style={{ paddingRight: '10px' }}>
            <div style={{ cursor: 'pointer' }}>
              <img
                className="edit"
                src="assets/edit-icon.svg"
                alt="Edit"
                onClick={() => {
                  setPrefixToUpdate(record);
                  setAddModalOpen(true);
                }}
              />
            </div>
          </Space>
        ),
    },
  ];

  const { status, data } = useQuery<any, AxiosError, any[]>({
    queryKey: [INVOICE_NUMBERS],
    queryFn: getInvoiceNumbers,
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
    refetchOnWindowFocus: false,
    onError: (error: AxiosError) => {
      const errorMessage =
        (error.response?.data as { Message?: string })?.Message ||
        'An error occurred';
      message.error(errorMessage);
    },
  });

  return (
    <>
      <div className="mb-[20px] flex w-full items-center justify-between">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.invoiceChars.InvoiceChars}
        </p>
        {!data?.length && (
          <button
            onClick={() => setAddModalOpen(true)}
            className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
          >
            <PlusOutlined />
            <p className="hidden md:block">
              {translation.invoiceChars.AddNewCharsButton}
            </p>
          </button>
        )}
      </div>
      <InvoicePrefixesForm
        open={addModalOpen}
        partOfInvoiceNameToUpdate={prefixToUpdate}
        handleClose={() => {
          setPrefixToUpdate(null);
          setAddModalOpen(false);
        }}
      />
      <STableWrapper>
        {status === 'loading' ? (
          <Skeleton active />
        ) : (
          <BasicTable
            sticky={{
              offsetHeader: 0,
            }}
            className="custom-bordered"
            bordered
            showHeader={true}
            data={data || []}
            columns={columns as any[]}
          />
        )}
      </STableWrapper>
    </>
  );
};

const STableWrapper = styled.div`
  margin-top: 24px;
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

export default IdentifierChars;
